export const GA_MEASUREMENT_ID = "G-ZYNCXYY83Q";
export const COOKIE_CONSENT_KEY = "cookie-consent";
const DEBUG = true; // set to false to silence GA logs

const log = (...args: unknown[]) => {
  if (DEBUG) console.log("%c[GA]", "color:#f59e0b;font-weight:bold", ...args);
};

export type ConsentValue = "all" | "necessary" | null;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const getConsent = (): ConsentValue =>
  (localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentValue);

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];

  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    } as unknown as (...args: unknown[]) => void;
  }
};

export const trackPageView = (path?: string) => {
  if (getConsent() !== "all") {
    log("trackPageView skipped – consent:", getConsent());
    return;
  }

  const pagePath = path ?? `${window.location.pathname}${window.location.search}`;
  log("trackPageView →", pagePath);

  ensureGtag();
  window.gtag?.("event", "page_view", {
    page_title: document.title,
    page_path: pagePath,
    page_location: window.location.href,
    send_to: GA_MEASUREMENT_ID,
  });
};

export const loadGoogleAnalytics = () => {
  ensureGtag();

  if (document.getElementById("gtag-script")) {
    log("gtag script already loaded");
    return;
  }

  log("loading gtag.js script…");
  const script = document.createElement("script");
  script.id = "gtag-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.onload = () => {
    log("gtag.js loaded ✓ – configuring", GA_MEASUREMENT_ID);
    window.gtag?.("js", new Date());
    window.gtag?.("config", GA_MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
    });

    if (getConsent() === "all") {
      log("consent=all → granting analytics_storage + initial pageview");
      window.gtag?.("consent", "update", { analytics_storage: "granted" });
      trackPageView();
    }
  };
  script.onerror = () => log("⚠ gtag.js failed to load (ad-blocker?)");

  document.head.appendChild(script);
};

export const enableAnalytics = () => {
  log("enableAnalytics called – consent:", getConsent());
  ensureGtag();
  window.gtag?.("consent", "update", { analytics_storage: "granted" });

  if (document.getElementById("gtag-script")) {
    log("script exists → sending pageview");
    trackPageView();
    return;
  }

  loadGoogleAnalytics();
};
